import { RxDietControllerService } from "@9amhealth/openapi";
import { Cubit } from "blac";
import { addSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import envVariables from "src/lib/envVariables";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { onUserSessionReset } from "src/lib/onUserSessionReset";

interface IframeMessage {
  type: string;
  data: {
    username: string;
    password: string;
  };
}

enum IframeMessageType {
  APP_LOADED = "app.loaded",
  APP_LOGIN = "app.login"
}

interface RxDietState {
  username?: string;
  password?: string;
}

export default class RxDietCubit extends Cubit<RxDietState> {
  private resetGeneration = 0;

  constructor() {
    super({});

    // This cubit is a singleton that survives logout. Drop the cached RxDiet
    // credentials when the user is cleared so the next user on this device
    // re-fetches their own account instead of reusing the previous user's.
    onUserSessionReset(this.reset);
  }

  private readonly reset = () => {
    // Bump the generation so any in-flight credential fetch/create started for
    // the previous user is invalidated and cannot emit or log in after the
    // switch (see the generation checks in the async flows below).
    this.resetGeneration += 1;
    this.emit({});
  };

  private readonly getRxDietUserCredentials = async (generation: number) => {
    if (!this.state.username || !this.state.password) {
      try {
        const { data } = await RxDietControllerService.getUserCredentials();

        if (generation !== this.resetGeneration) {
          return;
        }

        this.emit({
          ...this.state,
          username: data.username,
          password: data.password
        });
      } catch (e) {
        addSentryBreadcrumb(
          "errorHandler",
          "getRxDietUserCredentials error",
          "info",
          {
            error: e
          }
        );
      }
    }
  };

  private readonly createRxDietUserAccount = async (generation: number) => {
    try {
      const { data } = await RxDietControllerService.createUser();

      if (generation !== this.resetGeneration) {
        return;
      }

      this.emit({
        ...this.state,
        username: data.username,
        password: data.password
      });
    } catch (e) {
      reportErrorSentry(e);
    }
  };

  private readonly logInToRxDietAccount = (credentials: {
    username?: string;
    password?: string;
  }) => {
    const { username, password } = credentials;

    if (!username || !password) {
      const errorMessage =
        "Cannot log in to RxDiet account, no username and/or password available";
      reportErrorSentry(errorMessage);
      throw new Error(errorMessage);
    }

    this.sendMessageToIframe({
      type: IframeMessageType.APP_LOGIN,
      data: {
        username,
        password
      }
    });
  };

  private readonly sendMessageToIframe = (message: IframeMessage) => {
    const iframeDOMElement = document.getElementById(
      "rxdiet-app"
    ) as HTMLIFrameElement | null;

    iframeDOMElement?.contentWindow?.postMessage(
      JSON.stringify(message),
      new URL(envVariables.RXDIET_IFRAME_URL).origin
    );
  };

  private readonly handleMessageFromIframe = async (event: MessageEvent) => {
    if (event.origin !== new URL(envVariables.RXDIET_IFRAME_URL).origin) {
      return;
    }

    // Accept messages only from the currently mounted RxDiet iframe. A stale
    // iframe left over from a previous user shares the same origin but must not
    // be allowed to drive a login for the new user.
    const iframeDOMElement = document.getElementById(
      "rxdiet-app"
    ) as HTMLIFrameElement | null;

    if (!iframeDOMElement || event.source !== iframeDOMElement.contentWindow) {
      return;
    }

    try {
      const message = JSON.parse(event.data as string) as IframeMessage;

      // Try to log user in to RxDiet account when RxDiet app is loaded inside iframe
      if (message.type === IframeMessageType.APP_LOADED) {
        // Snapshot the generation for this flow; a user switch mid-flight bumps
        // it, so the checks below bail out before emitting or posting the
        // previous user's credentials.
        const generation = this.resetGeneration;

        await this.getRxDietUserCredentials(generation);

        if (generation !== this.resetGeneration) {
          return;
        }

        // If there are no RxDiet user credentials, create new user
        if (!this.state.username || !this.state.password) {
          await this.createRxDietUserAccount(generation);

          if (generation !== this.resetGeneration) {
            return;
          }
        }

        this.logInToRxDietAccount({
          username: this.state.username,
          password: this.state.password
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to handle message from iframe");
      reportErrorSentry(e);
    }
  };

  public readonly messageEventListener = (event: MessageEvent) => {
    void this.handleMessageFromIframe(event);
  };
}
